// ─── EditRoleModal.tsx ────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef } from 'react';
import { useModules } from '../context/ModulesContext';
import InfoModal from './InfoModal';
import './EditRoleModal.css';

type OrgRole = {
    id:          string;
    orgID:       string;
    name:        string;
    description: string;
    permissions: string[];
    isSystem:    boolean;
    isDefault:   boolean;
    defaultModule?: string;
    createdBy:   string;
    dateCreated: string;
    dateUpdated: string;
};

type Permission = {
  id:          string;
  key:         string;
  name:        string;
  description: string;
  category:    string;
  parentKey:   string | null;
  isGrantable: boolean;
};

type EffectiveEntitlements = {
    features: Record<string, { enabled: boolean; [key: string]: any }>;
    limits: Record<string, number>;
    defaultModule: string;
};

type EditRoleModalProps = {
    roleObj:              OrgRole | null;       // null = create mode
    availablePermissions: Permission[];          // fetched by parent, passed down
    entitlements:         EffectiveEntitlements | null;
    isOpen:               boolean;
    onClose:              () => void;
    onSave:               (role: OrgRole) => Promise<void>;
};

// FIX: no client-side ID — server assigns ID on create, empty string signals new record
const EMPTY_ROLE: OrgRole = {
    id:          '',
    orgID:       '',
    name:        '',
    description: '',
    permissions: [],
    isSystem:    false,
    isDefault:   false,
    createdBy:   '',
    dateCreated: '',
    dateUpdated: '',
};

export default function EditRoleModal({
    roleObj,
    availablePermissions,
    entitlements,
    isOpen,
    onClose,
    onSave,
}: EditRoleModalProps) {
    const { modules } = useModules();
    const [role, setRole]     = useState<OrgRole>(EMPTY_ROLE);
    const [mode, setMode]     = useState<'create' | 'edit'>('create');
    const [saving, setSaving] = useState(false);
    // Surfaced as an InfoModal pop-out, not an inline banner — an inline message
    // is easy to miss once the permission list is scrolled.
    const [error, setError]   = useState<{ title: string; message: string } | null>(null);
    const [selectedDefaultModule, setSelectedDefaultModule] = useState<string>('');
    const modalRef = useRef<HTMLDivElement>(null);

    // Sync form state when roleObj changes
    useEffect(() => {
        if (roleObj) {
            // FIX: filter against availablePermissions keys, not hardcoded ALL_PERMISSIONS
            const validKeys = new Set(availablePermissions.map(p => p.key));
            setRole({ ...roleObj, permissions: roleObj.permissions.filter(k => validKeys.has(k)) });
            setMode('edit');
        } else {
            setRole(EMPTY_ROLE);
            setMode('create');
        }
        // Hydrate defaultModule from role (per-role) or entitlements (org fallback)
        setSelectedDefaultModule(roleObj?.defaultModule || entitlements?.defaultModule || '');
        setError(null);
    }, [roleObj, availablePermissions, entitlements]);

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(e.target as Node)) onClose();
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handlePermissionToggle = (key: string) => {
        setRole(prev => {
            const has = prev.permissions.includes(key);
            const next = has
                ? prev.permissions.filter(k => k !== key)
                : [...prev.permissions, key];


            // Auto-adjust defaultModule based on new permissions
            const nextOptions = modules
                .filter(m => m.route && m.viewPermission && next.includes(m.viewPermission))
                .map(m => m.key);
            const addingChatbot = !has && key === 'chatbot';
            const removingChatbot = has && key === 'chatbot';

            if (addingChatbot) {
                setSelectedDefaultModule('chatbot');
            } else if (removingChatbot) {
                setSelectedDefaultModule('');
            } else {
                setSelectedDefaultModule(prev => nextOptions.includes(prev) ? prev : '');
            }

            return { ...prev, permissions: next };
        });
    };

    // Build dropdown options from selected permission checkboxes + entitled modules
    const defaultModuleOptions = modules
        .filter(m => {
            if (!m.route || !m.viewPermission) return false;
            if (!role.permissions.includes(m.viewPermission)) return false;
            // Must also be entitled (chatbot has no entitlementKey separate check)
            if (m.key === 'chatbot') return entitlements?.features?.['chatbot']?.enabled !== false;
            return m.entitlementKey === null || entitlements?.features?.[m.entitlementKey]?.enabled === true;
        })
        .sort((a, b) => a.displayOrder - b.displayOrder);

    // Mirrors the server's role validation (roleRouter.validateRoleFields) so the
    // admin gets plain-language feedback before the request is sent. Returns the
    // first problem found, or null when the form is good to save.
    const validateForm = (): { title: string; message: string } | null => {
        const name = role.name.trim().replace(/\s+/g, ' ');
        const desc = role.description.trim().replace(/\s+/g, ' ');

        if (!name) {
            return { title: 'Role Name', message: 'Please give this role a name' };
        }
        if (name.length < 2 || name.length > 100) {
            return { title: 'Role Name', message: 'Role names must be between 2 and 100 characters' };
        }
        if (!/^[a-zA-Z0-9\s\-_]+$/.test(name)) {
            return { title: 'Role Name', message: 'Role names can only use letters, numbers, spaces, hyphens and underscores' };
        }
        if (desc.length < 10) {
            return { title: 'Role Description', message: 'Please describe the role with 10 or more characters' };
        }
        if (desc.length > 500) {
            return { title: 'Role Description', message: 'Please shorten the description to 500 characters or less' };
        }
        if (!/^[a-zA-Z0-9\s\-_.,()!?'"]+$/.test(desc)) {
            return { title: 'Role Description', message: 'The description can only use letters, numbers, spaces and basic punctuation' };
        }
        if (role.permissions.length === 0) {
            return { title: 'Permissions Required', message: 'Please select at least one permission for this role' };
        }
        if (!selectedDefaultModule) {
            return { title: 'Default Module Required', message: 'Please select a default landing module' };
        }

        return null;
    };

    const handleSave = async () => {
        // Owner only ever updates its default module — the name/description/
        // permission rules below don't apply to it.
        if (!isOwnerRole) {
            const problem = validateForm();
            if (problem) { setError(problem); return; }
        } else if (!selectedDefaultModule) {
            setError({ title: 'Default Module Required', message: 'Please select a default landing module' });
            return;
        }
        setSaving(true);
        setError(null);
        try {
            // Store defaultModule in the role itself (per-role, not org-wide)
            const roleWithDefault = { ...role, defaultModule: selectedDefaultModule };
            if (isOwnerRole) {
                // Owner role: only update defaultModule via entitlements (org-level fallback)
                await fetch(`${process.env.REACT_APP_BACKEND_URL}/entitlements/default-module`, {
                    method: 'PUT',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ defaultModule: selectedDefaultModule }),
                });
                onClose();
            } else {
                await onSave(roleWithDefault);
            }
        } catch (err: any) {
            setError({
                title:   err.title   || 'Role Not Saved',
                message: err.message || "That role couldn't be saved. Please try again.",
            });
        } finally {
            setSaving(false);
        }
    };

    // Build permission key → module mapping from registry
    const permToModule: Record<string, typeof modules[0]> = {};
    for (const m of modules) {
        if (m.viewPermission) permToModule[m.viewPermission] = m;
        if (m.adminPermission) permToModule[m.adminPermission] = m;
    }

    // Split into User Access (viewPermissions) vs Admin Access (adminPermissions).
    // Each side also picks up sub-feature permissions hanging off its own slot:
    // profiles.sensitive → profiles.admin on the admin side, chatbot.data →
    // chatbot on the user side, so each renders nested under its parent group.
    //
    // The user side is additionally filtered on the permission's own category.
    // Sub-features of a view permission are NOT all user-facing — the legacy
    // chatbot rows (documents, queries, content.manage) also hang off `chatbot`
    // and would flood the picker. `category` is what the seed sets deliberately,
    // so it's the thing to trust here rather than parentage alone.
    const userAccess  = availablePermissions.filter(p => {
        const m = permToModule[p.key];
        if (m && m.viewPermission === p.key) return true;
        return (
            !!p.parentKey &&
            permToModule[p.parentKey]?.viewPermission === p.parentKey &&
            p.category === 'User Access'
        );
    });
    const adminAccess = availablePermissions.filter(p => {
        const m = permToModule[p.key];
        if (m && m.adminPermission === p.key) return true;
        if (
            p.parentKey &&
            permToModule[p.parentKey]?.adminPermission === p.parentKey
        ) {
            return true;
        }
        return false;
    });

    // Group by module group and sort by displayOrder, view before admin within group
    type PermGroup = { group: string; displayOrder: number; perms: Permission[] };

    const buildGroups = (perms: Permission[]): PermGroup[] => {
        const map: Record<string, PermGroup> = {};
        for (const p of perms) {
            // Direct module match first; fall back to parent-module lookup so
            // sub-feature permissions land in the same group as their parent.
            const m =
                permToModule[p.key] ||
                (p.parentKey ? permToModule[p.parentKey] : undefined);
            if (!m) continue;
            if (!map[m.name]) map[m.name] = { group: m.name, displayOrder: m.displayOrder, perms: [] };
            // Keep lowest displayOrder for group header sorting
            if (m.displayOrder < map[m.name].displayOrder) map[m.name].displayOrder = m.displayOrder;
            map[m.name].perms.push(p);
        }
        return Object.values(map).sort((a, b) => a.displayOrder - b.displayOrder);
    };

    const userGroups = buildGroups(userAccess);
    const adminGroups = buildGroups(adminAccess);

    const isOwnerRole = role.isSystem && role.name === 'Owner';
    // Member / Collaborator (any system role other than Owner): the name and
    // permission set are seeded and fixed, but the Description and Default
    // Landing Module stay editable. Owner keeps its defaultModule-only flow.
    const isFixedRole = role.isSystem && !isOwnerRole;
    const nameLocked  = isOwnerRole || isFixedRole || saving;  // role name
    const permsLocked = isOwnerRole || isFixedRole || saving;  // permission checkboxes
    const descLocked  = isOwnerRole || saving;                 // Owner desc locked; Member/Collab editable

    const renderPermRow = (perm: Permission, indent = false) => (
        <label
            key={perm.key}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem', cursor: permsLocked ? 'not-allowed' : 'pointer', paddingLeft: indent ? '1.2rem' : 0 }}
        >
            <input
                type="checkbox"
                checked={role.permissions.includes(perm.key)}
                onChange={() => handlePermissionToggle(perm.key)}
                disabled={permsLocked}
                style={{ accentColor: '#4caf50' }}
            />
            <span>
                <strong style={{ fontSize: '0.9rem' }}>{perm.name}</strong>
                {perm.description && (
                    <span style={{ color: '#888', fontSize: '0.8rem', marginLeft: '0.4rem' }}>
                        — {perm.description}
                    </span>
                )}
            </span>
        </label>
    );

    return (
        <div className="smrtdocs-modal-overlay" ref={modalRef}>
            <div className="smrtdocs-modal" style={{ width: '540px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                <div className="smrtdocs-modal-header">
                    <h2 style={{ margin: 0, fontSize: '1rem' }}>{mode === 'edit' ? 'Edit Role' : 'Create Role'}</h2>
                    {isOwnerRole && (
                        <button className="btn-cancel" onClick={onClose}>Close</button>
                    )}
                </div>

                <div className="smrtdocs-modal-body" style={{ overflowY: 'auto', flex: 1 }}>
                    {(isOwnerRole || isFixedRole) && (
                        <div style={{ background: '#2a2a2a', border: '1px solid #555', borderRadius: 6, padding: '8px 12px', marginBottom: '1rem', fontSize: '0.85rem', color: '#d4a017' }}>
                            System role — permissions cannot be modified
                        </div>
                    )}

                    <div style={{ marginBottom: '1rem' }}>
                        <div style={{ fontSize: '0.75rem', color: '#9aa0a6', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.25rem' }}>Role Name</div>
                        <input
                            type="text"
                            className="manage-input"
                            value={role.name}
                            onChange={e => setRole(prev => ({ ...prev, name: e.target.value }))}
                            disabled={nameLocked}
                            autoFocus
                            style={{ width: '100%' }}
                        />
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                        <div style={{ fontSize: '0.75rem', color: '#9aa0a6', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.25rem' }}>Description</div>
                        <input
                            type="text"
                            className="manage-input"
                            value={role.description}
                            onChange={e => setRole(prev => ({ ...prev, description: e.target.value }))}
                            disabled={descLocked}
                            placeholder="Describe what this role grants"
                            style={{ width: '100%' }}
                        />
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                        <div style={{ fontSize: '0.75rem', color: '#9aa0a6', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.25rem' }}>
                            Default Landing Module
                        </div>
                        <select
                            className="manage-input"
                            style={{ width: '100%' }}
                            value={selectedDefaultModule}
                            onChange={e => setSelectedDefaultModule(e.target.value)}
                            disabled={saving}
                        >
                            <option value="">— Select —</option>
                            {defaultModuleOptions.map(m => (
                                <option key={m.key} value={m.key}>{m.name}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <div style={{ fontSize: '0.75rem', color: '#9aa0a6', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>Permissions</div>
                        {availablePermissions.length === 0 ? (
                            <p style={{ color: '#666', fontSize: '0.85rem' }}>
                                No grantable permissions available for this org's plan.
                            </p>
                        ) : (
                            <>
                                {/* ── User Access ── */}
                                {userGroups.length > 0 && (
                                    <div style={{ marginBottom: '1rem' }}>
                                        <div style={{ fontSize: '0.75rem', color: '#007bff', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.4rem', borderBottom: '1px solid #2a2a2a', paddingBottom: '0.25rem' }}>
                                            User Access
                                        </div>
                                        {userGroups.map(g => (
                                            <div key={g.group} style={{ marginBottom: '0.5rem' }}>
                                                <div style={{ fontSize: '0.72rem', color: '#888', marginBottom: '0.2rem', marginTop: '0.3rem' }}>
                                                    {g.group}
                                                </div>
                                                {g.perms.map(perm =>
                                                    renderPermRow(
                                                        perm,
                                                        !!perm.parentKey && !permToModule[perm.key],
                                                    ),
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* ── Admin Access ── */}
                                {adminGroups.length > 0 && (
                                    <div style={{ marginBottom: '0.75rem' }}>
                                        <div style={{ fontSize: '0.75rem', color: '#d4a017', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.4rem', borderBottom: '1px solid #2a2a2a', paddingBottom: '0.25rem' }}>
                                            Admin Access
                                        </div>
                                        {adminGroups.map(g => (
                                            <div key={g.group} style={{ marginBottom: '0.5rem' }}>
                                                <div style={{ fontSize: '0.72rem', color: '#888', marginBottom: '0.2rem', marginTop: '0.3rem' }}>
                                                    {g.group}
                                                </div>
                                                {g.perms.map(perm =>
                                                    renderPermRow(
                                                        perm,
                                                        !!perm.parentKey && !permToModule[perm.key],
                                                    ),
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div style={{ padding: '12px 20px', display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #555' }}>
                    <button className="btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
                    <button className="btn-save" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving…' : mode === 'edit' ? 'Save Changes' : 'Create'}
                    </button>
                </div>
            </div>

            {/* Rendered inside this overlay so dismissing it isn't treated as a
                click outside the role modal. */}
            <InfoModal
                nested
                isOpen={!!error}
                title={error?.title || ''}
                message={error?.message || ''}
                onClose={() => setError(null)}
            />
        </div>
    );
}